import type { ExtractedProductInfo } from '../information-extraction/db/ExtractedProductInfo.js';
import type { MailExtractionResult } from '../information-extraction/db/MailExtractionResult.js';
import { SapFieldIssue, SapValidationReport } from './db/SapValidation.js';

/**
 * Thrown by the XML generation / SAP send path when an extraction result is
 * missing mandatory fields. Carries the full {@link SapValidationReport} so the
 * caller can persist it and surface the concrete problems to the user.
 */
export class SapValidationError extends Error {
  constructor(public readonly report: SapValidationReport) {
    const fields = report.errors.map(e => e.message).join(' ');
    super(`Angebot kann nicht an SAP übermittelt werden – Pflichtangaben fehlen. ${fields}`.trim());
    this.name = 'SapValidationError';
  }
}

export interface EffectiveProducts {
  /** corrected products merged over extracted products, plus newly created products */
  allProducts: ExtractedProductInfo[];
  /** the products that become real SAP positions (everything that is not an alternative) */
  explicitOrdered: ExtractedProductInfo[];
  /** alternatives that may substitute a main position lacking a material number */
  alternatives: ExtractedProductInfo[];
}

/**
 * Resolve the effective product set exactly the way {@link generateXML} does:
 * corrections replace their extracted counterparts, newly created products are
 * appended, and the set is split into explicit (ordered) positions vs.
 * alternatives. Kept here so validation and XML generation can never drift apart.
 */
export function resolveEffectiveProducts(result: MailExtractionResult): EffectiveProducts {
  const corrections = result.correctedProducts ?? [];
  // Removed positions are dropped entirely: their extracted counterpart is skipped (not
  // re-surfaced as the original), and removed new products vanish.
  const removedExtractedIds = new Set(
    corrections.filter(c => c.removed && c.extractedProductInfoId).map(c => c.extractedProductInfoId!),
  );
  const correctionsByExtractedId = new Map(
    corrections.filter(c => c.extractedProductInfoId && !c.removed).map(c => [c.extractedProductInfoId!, c]),
  );
  const newProducts = corrections.filter(c => !c.extractedProductInfoId && !c.removed);

  const allProducts: ExtractedProductInfo[] = [
    ...(result.extractedProducts ?? [])
      .filter(p => !removedExtractedIds.has(p._id))
      .map(p => correctionsByExtractedId.get(p._id) ?? p),
    ...newProducts,
  ];

  return {
    allProducts,
    explicitOrdered: allProducts.filter(p => !p.isAlternative),
    alternatives: allProducts.filter(p => p.isAlternative),
  };
}

const isBlank = (value: string | null | undefined) => !value || !value.trim();

/**
 * Validate an extraction result against the mandatory fields a valid SAP offer
 * requires. Pure and isomorphic (no DB access) so it can run on the client for
 * live highlighting and on the server as the export gate.
 *
 * Only fields that are essential to create an offer block the export:
 * requesting company debitor + name; at least one position; and per position a
 * material number (own or via a material-bearing alternative), a positive
 * quantity, a length type and — for non-FX positions — a positive length.
 * Optional context (contact person, certificate, delivery condition, desired
 * delivery date) is intentionally not validated here.
 */
export function validateExtractionForSap(result: MailExtractionResult): SapValidationReport {
  const errors: SapFieldIssue[] = [];

  const headerError = (field: string, message: string) =>
    errors.push(new SapFieldIssue({ scope: 'header', field, message }));

  // ---- Header / requesting company ----
  const rc = result.requestingCompany;
  if (!rc) {
    headerError('requestingCompany', 'Keine anfragende Firma erkannt.');
  } else {
    if (isBlank(rc.debitor)) headerError('debitor', 'Debitor (AG_PARTN) fehlt.');
    if (isBlank(rc.name)) headerError('name', 'Firmenname fehlt.');
  }

  // ---- Positions ----
  const { explicitOrdered, alternatives } = resolveEffectiveProducts(result);
  if (explicitOrdered.length === 0) {
    headerError('positions', 'Keine Positionen vorhanden.');
  }

  for (const product of explicitOrdered) {
    const pos = product.positionNumber ?? '?';
    const positionNumber = product.positionNumber ?? undefined;

    const error = (field: string, message: string) =>
      errors.push(new SapFieldIssue({ scope: 'position', positionNumber, productId: product._id, field, message: `Position ${pos}: ${message}` }));

    // A main position needs a material number, either its own or via a matching
    // alternative that carries one (the alternative is then exported in its place).
    const substitute = alternatives.find(a => a.alternativeFor === product.positionNumber && !isBlank(a.productNumber));
    if (isBlank(product.productNumber) && !substitute) {
      error('productNumber', 'Materialnummer fehlt.');
    }

    // Validate the product that will actually be exported (substitute if used).
    const exported = isBlank(product.productNumber) && substitute ? substitute : product;
    if (exported.quantity == null || exported.quantity <= 0) error('quantity', 'Menge fehlt oder ist 0.');
    if (isBlank(exported.lengthType)) error('lengthType', 'Längenart fehlt.');
    if (exported.lengthType !== 'FX' && (exported.length == null || exported.length <= 0))
      error('length', 'Länge fehlt oder ist 0.');
  }

  return new SapValidationReport({ ok: errors.length === 0, errors });
}

/**
 * Display-only validation. Returns everything {@link validateExtractionForSap}
 * blocks on, PLUS softer extraction-completeness issues that are not themselves
 * SAP send-gates but explain WHY an offer is incomplete: missing dimensions (no
 * outer diameter at all) and a missing material description. Drives the UI error
 * box and the red field highlighting.
 *
 * IMPORTANT: never use this to gate the SAP export — a missing diameter blocks
 * matching (so no material number is found, which validateExtractionForSap
 * already catches), but the extra issues here must not independently block a send
 * that is otherwise complete. Use {@link validateExtractionForSap} for gating.
 */
export function validateExtractionForDisplay(result: MailExtractionResult): SapValidationReport {
  const errors: SapFieldIssue[] = [...validateExtractionForSap(result).errors];

  const { explicitOrdered } = resolveEffectiveProducts(result);
  for (const product of explicitOrdered) {
    const pos = product.positionNumber ?? '?';
    const positionNumber = product.positionNumber ?? undefined;
    const add = (field: string, message: string) =>
      errors.push(
        new SapFieldIssue({ scope: 'position', positionNumber, productId: product._id, field, message: `Position ${pos}: ${message}` }),
      );

    if (product.outerDiameter1 == null && product.outerDiameter2 == null)
      add('outerDiameter1', 'Maße fehlen – mindestens ein Außendurchmesser erforderlich.');
    if (isBlank(product.material)) add('material', 'Material fehlt.');
  }

  return new SapValidationReport({ ok: errors.length === 0, errors });
}
